import {HttpClient} from "@angular/common/http";
import {Injectable} from "@angular/core";
import {ToastrService} from "ngx-toastr";
import {Observable} from "rxjs";
import {environment} from "../../../environments/environment";
import {BaseError} from "../../components/pages/base-error";
import {Service} from "../../models/service";
import {ExternalServer} from "../../models/external-server";
import Helpers from "../../utils/helpers";
import {RecoverOrphanConfig} from "../../models/recover-orphan-config";

@Injectable()
export class ServiceControllerService extends BaseError {

  private baseUrl: string = environment.BASE_URL + "service";

  constructor(private http: HttpClient,
              public toastr: ToastrService) {
    super(toastr);
  }

  public getServicesList(): Observable<Service[]> {
    return this.http.get(this.baseUrl) as Observable<Service[]>;
  }

  public getMyAccountService(): Observable<Service> {
    return this.http.get(`${this.baseUrl}/myaccountpage`) as Observable<Service>;
  }

  public getService(id: string) {
    return this.http.get(this.baseUrl + "/" + id);
  }

  public getServiceForms(id: string) {
    return this.http.get(this.baseUrl + "/" + id + "/forms");
  }

  public getServiceTutorials(id: string) {
    return this.http.get(this.baseUrl + "/" + id + "/tutorials");
  }

  public addService(service: Service) {
    Helpers.trimRecursiveObj(service);
    const body = JSON.stringify(service);
    return this.http.post(this.baseUrl, body);
  }

  public saveService(service: Service, externalServer: ExternalServer) {
    Helpers.trimRecursiveObj(service);
    const body = JSON.stringify(service);
    if (externalServer && externalServer.id) {
      this.baseUrl = this.baseUrl + "?externalServerId=" + externalServer.id;
    }
    return this.http.put(this.baseUrl, body, {responseType: "text"});
  }

  public activateService(serviceId: string, active: boolean) {
    const body = "";
    return this.http.put(this.baseUrl + "/" + serviceId + "/activate/" + active, body);
  }

  public activateServiceExternal(serviceId: string, externalServer: any) {
    const body = "";
    return this.http.put(this.baseUrl + "/" + serviceId + "/activate/" + externalServer.active + "?externalServerId=" + externalServer.externalServer.id, body);
  }

  public deleteService(id: string) {
    return this.http.delete(this.baseUrl + "/" + id, {responseType: "text"});
  }

  public publishService(serviceId: string, serverId: string, title: string, description: string, typesOfPublication: any[] = []) {
    const json = {
      externalServerId: serverId,
      title,
      description,
      typesOfPublication};
    const body = JSON.stringify(json);
    return this.http.post(this.baseUrl + "/" + serviceId + "/publish", body);
  }

  public getServiceFile(serviceId: string, fileId: string) {
    return this.http.get(this.baseUrl + "/" + serviceId + "/file/" + fileId);
  }

  public downloadServiceFile(serviceId: string, fileId: string) {
    return this.http.get(this.baseUrl + "/" + serviceId + "/file/" + fileId + "/binary", {responseType: "blob"});
  }

  public deleteServiceFile(serviceId: string, fileId: string) {
    return this.http.delete(this.baseUrl + "/" + serviceId + "/file/" + fileId, {responseType: "text"});
  }

  public getFile(fileId: string) {
      return this.http.get(this.baseUrl + "/file/" + fileId);
  }

  public getFileFromURL(fileId: string) {
      return this.http.get(fileId);
  }

  public downloadFile(fileId: string) {
    return this.http.get(this.baseUrl + "/file/" + fileId + "/binary", {responseType: "blob"});
  }

  public deleteFile(fileId: string) {
    return this.http.delete(this.baseUrl + "/file/" + fileId, {responseType: "text"});
  }

  public deleteServiceLogo(serviceId: string) {
    return this.http.delete(this.baseUrl + "/" + serviceId + "/logo", {responseType: "text"});
  }

  public getServicesHistoryList(serviceId: string): Observable<any[]> {
    return this.http.get(this.baseUrl + "/" + serviceId + "/publishhistory") as Observable<any[]>;
  }

  public getHistoryPage(serviceId: string, page: number): Observable<any[]> {
    return this.http.get(this.baseUrl + "/" + serviceId + "/pagedpublishhistory?pageNo=" + page + "&pageSize=10") as Observable<any[]>;
  }

  public getSnapshotPage(serviceId: string, page: number): Observable<any[]> {
    return this.http.get(this.baseUrl + "/" + serviceId + "/snapshots?pageNo=" + page + "&pageSize=10") as Observable<any[]>;
  }

  public getSnapshotFile(serviceId: string, snapshotId: string): Observable<Blob> {
    return this.http.get(this.baseUrl + "/" + serviceId + "/snapshots/" + snapshotId + "/file", { responseType: "blob" }) as Observable<Blob>;
  }

  public updateSortOrder(services: Service[]) {
    const body = JSON.stringify(services);
    return this.http.put(environment.BASE_URL + "sortservices", body, {responseType: "text"});
  }

  public refreshAllInstances(): Observable<any> {
    return this.http.get(environment.BASE_URL + "refresh/services") as Observable<any>;
  }

  public getServiceEvents(serviceId: string): Observable<any> {
    return this.http.get(this.baseUrl + "/" + serviceId + "/service_event") as Observable<any>;
  }

  public addServiceEvent(serviceId: string, eventName: string) {
    const json = {name: eventName.trim()};
    const body = JSON.stringify(json);
    return this.http.post(this.baseUrl + "/" + serviceId + "/service_event", body);
  }

  public archiveService(service: Service) {
    const body = JSON.stringify(service);
    return this.http.put(this.baseUrl + "/archive", body, {responseType: "text"});
  }

  public recoverOrphanConfig(serviceId: string): Observable<RecoverOrphanConfig[]> {
    return this.http.post(this.baseUrl + "/" + serviceId + "/recover-orphan-config", {}) as Observable<RecoverOrphanConfig[]>;
  }

}
